import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import Reveal from "../Reveal";
import { motion, AnimatePresence } from "framer-motion";

// 6 real client work images from projeler
const WORKS = [
  {
    id: 1,
    img: "/projeler/postunhazir_1776700579_3879568086261997846_40209662941.jpg",
    catKey: "creative",
    title: { tr: "Kampanya Kreatifleri", en: "Campaign Creatives" },
    desc: { tr: "Marka sesini güçlendiren sosyal medya kampanyaları.", en: "Social media campaigns that amplify brand voice." },
  },
  {
    id: 2,
    img: "/projeler/postunhazir_1746276095_3624349039141178197_40209662941.jpg",
    catKey: "social",
    title: { tr: "Ürün Tanıtımı", en: "Product Launch" },
    desc: { tr: "Ürün lansmanlarına özel kreatif içerik stratejisi.", en: "Creative content strategy for product launches." },
  },
  {
    id: 3,
    img: "/projeler/postunhazir_1746276095_3624349039141175670_40209662941.jpg",
    catKey: "creative",
    title: { tr: "Kreatif Tasarım", en: "Creative Design" },
    desc: { tr: "Markanızın kimliğini yansıtan görsel içerikler.", en: "Visual content reflecting your brand identity." },
  },
  {
    id: 4,
    img: "/projeler/postunhazir_1733155802_3514288033514426975_40209662941.jpg",
    catKey: "social",
    title: { tr: "Sosyal Medya Yönetimi", en: "Social Media Management" },
    desc: { tr: "Günlük içerik üretimi ve hesap yönetimi.", en: "Daily content production and account management." },
  },
  {
    id: 5,
    img: "/projeler/postunhazir_1776700579_3879568086815659792_40209662941.jpg",
    catKey: "creative",
    title: { tr: "Reels & Video İçerik", en: "Reels & Video Content" },
    desc: { tr: "Organik büyümeyi destekleyen video serisi.", en: "Video series supporting organic growth." },
  },
  {
    id: 6,
    img: "/projeler/postunhazir_1776791747_3880332871433880364_40209662941.jpg",
    catKey: "turizm",
    title: { tr: "Turizm & Otel İçerikleri", en: "Tourism & Hotel Content" },
    desc: { tr: "Turizm markalarına özel hikaye anlatımı.", en: "Storytelling tailored for tourism brands." },
  },
];

const CAT_LABELS = {
  tr: { creative: "Kreatif", social: "Sosyal Medya", turizm: "Turizm" },
  en: { creative: "Creative", social: "Social Media", turizm: "Tourism" },
};

// Lightbox Gallery Component with touch swipe
function WorksLightbox({ index, onClose, onPrev, onNext, lang }) {
  const lk = lang === "tr" ? "tr" : "en";
  const work = WORKS[index];
  const touchStartX = React.useRef(null);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) { dx < 0 ? onNext() : onPrev(); }
    touchStartX.current = null;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-cream transition-colors z-10"
        aria-label="Kapat"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-cream/60 text-sm">
        {index + 1} / {WORKS.length}
      </div>

      {/* Prev button */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 sm:left-8 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-cream transition-colors"
        aria-label="Önceki"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>

      {/* Image */}
      <motion.div
        key={index}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-2xl w-full"
      >
        <img
          src={work.img}
          alt={work.title[lk]}
          className="w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl"
        />
        <div className="mt-4 text-center">
          <h3 className="text-cream font-display font-bold text-h3">{work.title[lk]}</h3>
          <p className="mt-1 text-cream/60 text-sm">{work.desc[lk]}</p>
        </div>
      </motion.div>

      {/* Next button */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-4 sm:right-8 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-cream transition-colors"
        aria-label="Sonraki"
      >
        <ChevronRight className="h-6 w-6" />
      </button>
    </motion.div>
  );
}

export default function WorksGrid() {
  const { lang } = useLanguage();
  const lk = lang === "tr" ? "tr" : "en";
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const openLightbox = (idx) => setLightboxIdx(idx);
  const closeLightbox = () => setLightboxIdx(null);
  const prevItem = () => setLightboxIdx((i) => (i > 0 ? i - 1 : WORKS.length - 1));
  const nextItem = () => setLightboxIdx((i) => (i < WORKS.length - 1 ? i + 1 : 0));

  return (
    <section
      id="works-grid"
      data-testid="works-section"
      className="relative bg-cream py-section sm:py-section-lg border-t border-mist"
    >
      <div className="container-page">
        <Reveal as="div" className="max-w-2xl mb-10">
          <span className="eyebrow">
            <span className="h-1 w-1 rounded-full bg-brand-pink" />
            {lang === "tr" ? "SEÇİLİ ÇALIŞMALAR" : "SELECTED WORKS"}
          </span>
          <h2 className="mt-5 font-display text-display-2 font-semibold text-ink">
            {lang === "tr" ? "Markaları büyüten " : "Works that scale "}
            <span className="gold-foil-text">{lang === "tr" ? "kreatif işler." : "brands."}</span>
          </h2>
          <p className="mt-4 max-w-md text-body-lg text-ink/65 leading-relaxed">
            {lang === "tr"
              ? "Sosyal medyadan video prodüksiyona, 200+ projede kanıtlanmış kreatif çözümler."
              : "From social media to video production — creative solutions proven in 200+ projects."}
          </p>
        </Reveal>

        {/* 3x2 photo grid — clickable for lightbox */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-5">
          {WORKS.map((w, i) => (
            <Reveal key={w.id} as="div" delay={i * 0.06}>
              <button
                type="button"
                onClick={() => openLightbox(i)}
                data-testid={`work-card-${w.id}`}
                className="group relative w-full block overflow-hidden rounded-2xl bg-mist border border-mist
                           transition-[transform,border-color,box-shadow] duration-500 ease-out-quart
                           hover:-translate-y-1 hover:border-brand-pink/60 hover:shadow-card cursor-zoom-in"
              >
                <div className="relative aspect-square overflow-hidden bg-ink/5">
                  <img
                    src={w.img}
                    alt={w.title[lk]}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out-quart group-hover:scale-105"
                  />
                  {/* Category badge */}
                  <span className="absolute top-3 left-3 rounded-full bg-cream/90 backdrop-blur-sm text-ink text-[0.6rem] uppercase tracking-[0.14em] font-bold px-2 py-1">
                    {CAT_LABELS[lk]?.[w.catKey] || w.catKey}
                  </span>
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute inset-x-4 bottom-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-[transform,opacity] duration-300">
                    <h3 className="font-display text-sm font-bold text-cream leading-snug">{w.title[lk]}</h3>
                  </div>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        {/* Tümünü Gör — below grid */}
        <div className="mt-10 flex justify-center">
          <Link
            to={`/${lang}/works`}
            data-testid="works-see-all"
            className="inline-flex items-center gap-2 rounded-full bg-ink text-cream px-8 py-3.5 text-sm font-semibold hover:bg-ink/80 transition-colors"
          >
            {lang === "tr" ? "Tüm Çalışmalarımızı Gör" : "See All Works"}
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIdx !== null && (
          <WorksLightbox
            index={lightboxIdx}
            onClose={closeLightbox}
            onPrev={prevItem}
            onNext={nextItem}
            lang={lang}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
